'use client'

import { addressFormatter, defaultDrops, getLatLng, ITravelPointQuote } from '@repo/sdk'
import {
  Button,
  DateTimeInput,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@repo/ui'
import { Input } from '@repo/ui/components'
import { useDropsContext } from '@repo/ui/forms'
import { LocationInput } from '@repo/ui/inputs'
import { useQuery } from '@tanstack/react-query'
import { CircleMinus, CirclePlus } from 'lucide-react'
import { useState } from 'react'
import { Control, useController, useFormContext } from 'react-hook-form'

interface Props {
  control: Control<{ drops: ITravelPointQuote[] }>
  drop: ITravelPointQuote
  index: number
  isAdmin?: boolean
}

export const AdminLocationAndDate = ({ control, index, isAdmin = false }: Props) => {
  const { getFieldState } = useFormContext<{ drops: ITravelPointQuote[] }>()
  const [isManual, setIsManual] = useState(false)

  const { drops, insertDrop, removeDrop } = useDropsContext()

  const isIndex = index !== -1

  const handleAddStop = () => {
    isIndex && insertDrop(index + 1, defaultDrops(drops.length))
  }

  const dateState = getFieldState(`drops.${index}.date`)
  const timeState = getFieldState(`drops.${index}.time`)

  const isDateTimeError = [dateState, timeState].map(state => state.error).filter(Boolean)

  return (
    <div className={'flex w-full grow gap-1.5'}>
      <div className={'flex w-full flex-col'}>
        <div className={'flex w-full flex-col gap-6'}>
          <div className={'flex flex-col items-start gap-3'}>
            <FormField
              control={control}
              name={`drops.${index}.place`}
              render={({ field }) => {
                return (
                  <FormItem className={'w-full'}>
                    <FormLabel className={'w-full'}>Company or Location</FormLabel>
                    <FormControl className={'w-full'}>
                      <LocationInput {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )
              }}
            />
            {isManual ? (
              <>
                <FormField
                  control={control}
                  name={`drops.${index}.place.address_book.name`}
                  render={({ field }) => (
                    <FormItem className={'w-full'}>
                      <FormLabel className={'w-full'}>Company</FormLabel>
                      <FormControl className={'w-full'}>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name={`drops.${index}.place.place_name`}
                  render={({ field }) => (
                    <FormItem className={'w-full'}>
                      <FormLabel className={'w-full'}>Street Address</FormLabel>
                      <FormControl className={'w-full'}>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className={'flex gap-3'}>
                  <FormField
                    control={control}
                    name={`drops.${index}.place.city`}
                    render={({ field }) => (
                      <FormItem className={'w-full'}>
                        <FormLabel className={'w-full'}>City</FormLabel>
                        <FormControl className={'w-full'}>
                          <Input {...field} value={field.value ?? undefined} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={control}
                    name={`drops.${index}.place.postcode`}
                    render={({ field }) => (
                      <FormItem className={'w-full'}>
                        <FormLabel className={'w-full'}>Post Code</FormLabel>
                        <FormControl className={'w-full'}>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </>
            ) : (
              <Button
                className={'h-3.5 p-0 text-sm text-[#6327E0]'}
                onClick={() => setIsManual(true)}
                type={'button'}
                variant={'link'}
              >
                Manually enter address
              </Button>
            )}
          </div>

          <PlaceDetailsBox control={control} index={index} />

          <FormItem className={'flex flex-col'}>
            <FormLabel>Date & time</FormLabel>
            <FormControl>
              {/*@ts-ignore*/}
              <DateTimeInput control={control} index={index} isAdmin={isAdmin} />
            </FormControl>
            {isDateTimeError.map((error, index) => (
              <FormMessage key={error!.ref?.name ?? `error_date_time_${index}`}>
                {error!.message}
              </FormMessage>
            ))}
          </FormItem>
        </div>
        <div>
          <Button className={'grow-0'} onClick={handleAddStop} type={'button'} variant={'ghost'}>
            <CirclePlus /> Add stop
          </Button>
          {drops.length > 2 && (
            <Button onClick={() => isIndex && removeDrop(index)} type={'button'} variant={'ghost'}>
              <CircleMinus /> Remove stop
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

const PlaceDetailsBox = ({
  control,
  index,
}: {
  control: Control<{ drops: ITravelPointQuote[] }>
  index: number
}) => {
  const { field } = useController({ control, name: `drops.${index}.place` })

  const { data: addressData } = useQuery({
    async queryFn() {
      if (!field.value) return

      return getLatLng(field.value.place_id ?? '')
    },
    queryKey: ['google/place/details', field.value],
  })

  if (!field.value) return null

  const formattedAddress = addressFormatter(addressData?.address_components)

  return (
    <div className={'bg-[#F4F7FB] p-2.5 text-sm font-semibold'}>
      <p>
        {formattedAddress?.premise} {formattedAddress?.address}
      </p>
      <p>
        {formattedAddress?.city} {formattedAddress?.postcode}
      </p>
      <p>{formattedAddress?.countryCode}</p>
    </div>
  )
}
